import { FC, memo, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { useSnackbar } from "notistack"
import { Modal } from "@/core/components"
import { getApiErrorMessage } from "@/core/utils"
import { describeBatchReason } from "../../constants/batchReasons"
import { useClientsLookup } from "../../hooks"
import {
  UserbotBatches,
  UserbotCatalog,
  UserbotTemplates,
} from "../../interfaces"
import {
  useCreateAccountBatchMutation,
  useRolesQuery,
  useTemplatesQuery,
} from "../../store"
import { FieldSpecForm } from "../fields/fieldSpecForm"
import { coerceFieldValue, rawFromFieldValue } from "../fields/fieldSpecCoerce"
import { validateFieldValues } from "../fields/fieldSpecValidate"
import { BatchTargetMode, buildCreateBatchDto } from "./batchDto"

const STEPS = ["Действие", "Параметры", "Цели"]
const FETCH_LIMIT = 200
const SESSION_STAGGER_MIN = 60000
const SESSION_STAGGER_PREFILL = 300000
const SESSION_STAGGER_HINT =
  "Для запасных сессий требуется ≥ 60 000 мс; рекомендовано 300 000–900 000"

interface IProps {
  /** Выделенные в таблице аккаунтов clientId. */
  selectedClientIds: string[]
  onClose: () => void
  /** Успешный запуск: таблица снимает выделение и закрывает модалку (аналог D-15). */
  onLaunched: () => void
}

/** Prefill значений переменных из FieldSpec.default — паттерн templateRunForm.tsx. */
const initialInputs = (
  variables: UserbotTemplates.TemplateVariable[],
): Record<string, unknown> => {
  const inputs: Record<string, unknown> = {}
  for (const v of variables) {
    inputs[v.key] =
      v.default !== undefined
        ? rawFromFieldValue(v, v.default)
        : v.type === "array"
          ? []
          : v.type === "boolean"
            ? "false"
            : ""
  }
  return inputs
}

const isEmptyValue = (spec: UserbotCatalog.FieldSpec, raw: unknown): boolean =>
  spec.type === "array"
    ? !Array.isArray(raw) || raw.length === 0
    : raw === "" || raw == null

const WizardForm: FC<IProps> = ({
  selectedClientIds,
  onClose: _onClose,
  onLaunched,
}) => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { getClient } = useClientsLookup()
  const [step, setStep] = useState(0)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [templateSearch, setTemplateSearch] = useState("")
  const [inputs, setInputs] = useState<Record<string, unknown>>({})
  const [staggerMs, setStaggerMs] = useState<number | string>(0)
  const [targetMode, setTargetMode] = useState<BatchTargetMode>(
    selectedClientIds.length > 0 ? "selected" : "all",
  )
  const [role, setRole] = useState("")
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [dryRun, setDryRun] =
    useState<UserbotBatches.AccountBatchDryRunRes | null>(null)

  const { data: templates } = useTemplatesQuery({
    kind: "chain",
    limit: FETCH_LIMIT,
  })
  const { data: roles } = useRolesQuery({ limit: FETCH_LIMIT })
  const [createBatch, { isLoading: isLaunching }] =
    useCreateAccountBatchMutation()
  const [previewBatch, { isLoading: isPreviewing }] =
    useCreateAccountBatchMutation()

  const templatesBlindSpot =
    !!templates && templates.total > templates.items.length

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase()
    return (templates?.items ?? [])
      .filter((t) => t.isActive)
      .filter((t) => !q || t.name.toLowerCase().includes(q))
  }, [templates, templateSearch])

  const selectedTemplate =
    templates?.items.find((t) => t.id === templateId) ?? null
  const isSession = selectedTemplate?.category === "session"

  const fieldErrors = useMemo(
    () =>
      selectedTemplate
        ? validateFieldValues(selectedTemplate.variables, inputs)
        : {},
    [selectedTemplate, inputs],
  )

  const coercedParams = useMemo(() => {
    const out: Record<string, unknown> = {}
    if (!selectedTemplate) return out
    for (const v of selectedTemplate.variables) {
      const raw = inputs[v.key]
      if (!v.required && isEmptyValue(v, raw)) continue
      out[v.key] = coerceFieldValue(v, raw)
    }
    return out
  }, [selectedTemplate, inputs])

  const staggerValid =
    staggerMs !== "" &&
    Number.isFinite(Number(staggerMs)) &&
    Number(staggerMs) >= 0 &&
    (!isSession || Number(staggerMs) >= SESSION_STAGGER_MIN)

  const targetValid =
    targetMode === "selected"
      ? selectedClientIds.length > 0
      : targetMode === "role"
        ? !!role
        : true

  const stepValid = [
    !!templateId && !templatesBlindSpot,
    !!selectedTemplate && Object.keys(fieldErrors).length === 0 && staggerValid,
    targetValid,
  ][step]

  const handleTemplateChange = (id: string) => {
    setTemplateId(id)
    setSubmitError(null)
    const template = templates?.items.find((t) => t.id === id) ?? null
    setInputs(template ? initialInputs(template.variables) : {})
    setStaggerMs(template?.category === "session" ? SESSION_STAGGER_PREFILL : 0)
    setDryRun(null)
  }

  const goTo = (next: number) => {
    setSubmitError(null)
    setStep(next)
  }

  const handleDryRun = async () => {
    if (!selectedTemplate) return
    setSubmitError(null)
    try {
      const res = await previewBatch({
        body: buildCreateBatchDto({
          templateId: selectedTemplate.id,
          targetMode,
          selectedClientIds,
          role,
          params: coercedParams,
          staggerMs:
            typeof staggerMs === "number" ? staggerMs : Number(staggerMs) || 0,
          dryRun: true,
        }),
      }).unwrap()
      if (UserbotBatches.isDryRunRes(res)) {
        setDryRun(res)
      } else {
        setSubmitError("Сервер вернул job вместо dryRun-ответа")
      }
    } catch (e) {
      setSubmitError(getApiErrorMessage(e))
    }
  }

  const handleLaunch = async () => {
    if (!selectedTemplate) return
    setSubmitError(null)
    try {
      const res = await createBatch({
        body: buildCreateBatchDto({
          templateId: selectedTemplate.id,
          targetMode,
          selectedClientIds,
          role,
          params: coercedParams,
          staggerMs:
            typeof staggerMs === "number" ? staggerMs : Number(staggerMs) || 0,
        }),
      }).unwrap()
      if (UserbotBatches.isDryRunRes(res)) {
        setSubmitError("Сервер вернул dryRun-ответ вместо создания запуска")
        return
      }
      enqueueSnackbar("Запуск создан", { variant: "success" })
      navigate(`/userbot/batches/${res.id}`)
      onLaunched()
    } catch (e) {
      const status = (e as { status?: number }).status
      if (status === 403) {
        setSubmitError("Нет прав на запуск")
      } else if (status === 404) {
        setSubmitError("Шаблон не найден. Выберите другой шаблон")
      } else {
        setSubmitError(getApiErrorMessage(e))
      }
    }
  }

  return (
    <Stack spacing={2}>
      <Stepper activeStep={step} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && (
        <Stack spacing={1}>
          {templatesBlindSpot && templates && (
            <Alert severity="warning">
              {`Загружены не все шаблоны (${templates.items.length} из ${templates.total}). Запуск недоступен.`}
            </Alert>
          )}
          <TextField
            size="small"
            label="Поиск"
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
          />
          <Box sx={{ maxHeight: 320, overflow: "auto" }}>
            <RadioGroup
              value={templateId ?? ""}
              onChange={(_, v) => handleTemplateChange(v)}
            >
              {filteredTemplates.map((t) => (
                <Box key={t.id}>
                  <FormControlLabel
                    value={t.id}
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>{t.name}</Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t.category}
                        />
                      </Stack>
                    }
                  />
                  {t.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ pl: 4 }}
                      noWrap
                      title={t.description}
                    >
                      {t.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </RadioGroup>
          </Box>
        </Stack>
      )}

      {step === 1 && (
        <Stack spacing={2}>
          {selectedTemplate && selectedTemplate.variables.length === 0 ? (
            <Typography color="text.secondary">
              Шаблон не требует параметров
            </Typography>
          ) : (
            selectedTemplate && (
              <FieldSpecForm
                specs={selectedTemplate.variables}
                values={inputs}
                onChange={setInputs}
                errors={fieldErrors}
              />
            )
          )}
          <TextField
            label="Пауза между запусками, мс"
            type="number"
            inputProps={{ min: 0 }}
            value={staggerMs}
            onChange={(e) => {
              if (e.target.value === "") {
                setStaggerMs("")
                return
              }
              const next = Number(e.target.value)
              if (Number.isNaN(next)) return
              setStaggerMs(next)
            }}
            helperText={isSession ? SESSION_STAGGER_HINT : "По умолчанию 0"}
          />
        </Stack>
      )}

      {step === 2 && (
        <Stack spacing={2}>
          <RadioGroup
            value={targetMode}
            onChange={(e) => setTargetMode(e.target.value as BatchTargetMode)}
          >
            <FormControlLabel
              value="selected"
              disabled={selectedClientIds.length === 0}
              control={<Radio />}
              label={`Отмеченные клиенты (${selectedClientIds.length})`}
            />
            <FormControlLabel
              value="role"
              control={<Radio />}
              label="По роли"
            />
            <FormControlLabel value="all" control={<Radio />} label="Все" />
          </RadioGroup>
          {targetMode === "role" && (
            <TextField
              select
              label="Роль"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
            >
              {(roles?.items ?? [])
                .filter((r) => r.isActive)
                .map((r) => (
                  <MenuItem key={r.id} value={r.name}>
                    {r.name}
                  </MenuItem>
                ))}
            </TextField>
          )}
          {dryRun && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                mb={1}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Всего: ${dryRun.counters.total}`}
                />
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`Запустится: ${dryRun.counters.wouldRun}`}
                />
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`Пропущено: ${dryRun.counters.skipped}`}
                />
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Клиент</TableCell>
                    <TableCell>Pre-check</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dryRun.items.map((item) => {
                    const client = getClient(item.clientId)
                    const preCheckView = describeBatchReason(
                      item.preCheck === "ok" ? null : item.preCheck,
                    )
                    return (
                      <TableRow key={item.clientId}>
                        <TableCell>
                          {client?.name ?? item.clientId.slice(0, 8)}
                        </TableCell>
                        <TableCell>
                          {item.preCheck === "ok" ? (
                            <Chip
                              size="small"
                              color="success"
                              label="Пройдёт"
                            />
                          ) : (
                            <Chip
                              size="small"
                              color="warning"
                              label={preCheckView.label}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
          {submitError && (
            <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
              {submitError}
            </Alert>
          )}
        </Stack>
      )}

      {submitError && step !== 2 && (
        <Alert severity="error">{submitError}</Alert>
      )}
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Button disabled={step === 0} onClick={() => goTo(step - 1)}>
          Назад
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            disabled={!stepValid}
            onClick={() => goTo(step + 1)}
          >
            Далее
          </Button>
        ) : (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              disabled={!stepValid || isPreviewing}
              onClick={handleDryRun}
            >
              Предпросмотр (dryRun)
              {isPreviewing && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Button>
            <Button
              variant="contained"
              disabled={!stepValid || isLaunching}
              onClick={handleLaunch}
            >
              Запустить
            </Button>
          </Box>
        )}
      </Box>
    </Stack>
  )
}

export const BatchLaunchWizard: FC<IProps> = memo(
  function BatchLaunchWizard(props) {
    const { onClose } = props
    return (
      <Modal
        title="Запуск действия по аккаунтам"
        onClose={onClose}
        disableBackdropClose
      >
        <WizardForm {...props} />
      </Modal>
    )
  },
)
